import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { allCategories } from "@/utils/data";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword("");
    setMenuOpen(false);
  };

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const navLinkClass = (path: string) =>
    `px-3 py-2 text-sm rounded-md transition-colors ${
      isActive(path)
        ? "text-primary-700 font-medium bg-primary-50"
        : "text-foreground-700 hover:text-primary-700 hover:bg-background-100"
    }`;

  return (
    <header
      className={`sticky top-0 z-40 w-full bg-background-50/95 backdrop-blur border-b transition-colors ${
        scrolled ? "border-background-200" : "border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-[72px] gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-leaf-fill text-lg"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-bold text-foreground-950 tracking-wide">自慰</span>
              <span className="text-[10px] text-foreground-500 tracking-widest mt-0.5">SELF-CARE</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <Link to="/" className={navLinkClass("/")}>
              首页
            </Link>
            <div
              className="relative"
              onMouseEnter={() => setCategoryOpen(true)}
              onMouseLeave={() => setCategoryOpen(false)}
            >
              <button
                type="button"
                onClick={() => setCategoryOpen((prev) => !prev)}
                className={`flex items-center gap-1 px-3 py-2 text-sm rounded-md transition-colors ${
                  isActive("/category")
                    ? "text-primary-700 font-medium bg-primary-50"
                    : "text-foreground-700 hover:text-primary-700 hover:bg-background-100"
                }`}
              >
                分类
                <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? "rotate-180" : ""}`}></i>
              </button>
              {categoryOpen && (
                <div className="absolute left-0 top-full pt-2 w-56">
                  <div className="bg-background-50 rounded-lg border border-background-200 p-2">
                    {allCategories.map((category) => (
                      <Link
                        key={category.id}
                        to={`/category/${category.id}`}
                        className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-700"
                      >
                        <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                          <i className={category.icon}></i>
                        </span>
                        {category.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Link to="/topic/topic-1" className={navLinkClass("/topic")}>
              专题
            </Link>
            <Link to="/about" className={navLinkClass("/about")}>
              关于我们
            </Link>
            <Link to="/contact" className={navLinkClass("/contact")}>
              联系反馈
            </Link>
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={handleSearch} className="hidden lg:flex items-center">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  type="text"
                  name="keyword"
                  placeholder="搜索健康资讯…"
                  className="w-52 xl:w-60 h-9 pl-9 pr-3 text-sm rounded-full bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                />
              </div>
            </form>
            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4">
          <form onSubmit={handleSearch} className="relative mb-4">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              type="text"
              name="keyword"
              placeholder="搜索健康资讯…"
              className="w-full h-10 pl-9 pr-3 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </form>
          <nav className="flex flex-col gap-1">
            <Link to="/" className="px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100">
              首页
            </Link>
            <p className="px-3 pt-3 pb-1 text-xs text-foreground-400">分类</p>
            {allCategories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100"
              >
                <i className={`${category.icon} text-primary-600`}></i>
                {category.name}
              </Link>
            ))}
            <p className="px-3 pt-3 pb-1 text-xs text-foreground-400">更多</p>
            <Link to="/topic/topic-1" className="px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100">
              专题
            </Link>
            <Link to="/about" className="px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100">
              关于我们
            </Link>
            <Link to="/contact" className="px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100">
              联系反馈
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}